import React, { useEffect, useState } from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Snackbar from 'components/DesignSystem/Snackbar';
import DatePicker from 'components/DatePicker';
import Section from 'components/Meteorology/Shared/ControlPanelSheet/Section';
import OptionRow from 'components/Meteorology/Shared/ControlPanelSheet/OptionRow';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import useVariables from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useVariables';
import usePeriod from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/usePeriod';

import './index.scss';

type SheetSection = 'variable' | 'period' | '';

export default function ControlPanelSheet({
  open,
  dismiss,
}: {
  open: boolean;
  dismiss: () => void;
}) {
  const [openSection, setOpenSection] = useState<SheetSection>('variable');

  const { variables, variable, setVariable } = useVariables();
  const {
    startDate, endDate, dateIntervals, changePeriod, changePeriodRange, selectsRange, disabled,
  } = usePeriod();

  useEffect(() => {
    if (open) setOpenSection('variable');
  }, [open]);

  const toggleSection = (section: SheetSection) => () => (
    setOpenSection((current) => (current === section ? '' : section))
  );

  const variableLabel = variables.find((v) => v.value === variable)?.label;
  const periodLabel = (() => {
    if (!startDate) return undefined;
    const start = startDate.toLocaleDateString('pt-BR');
    if (!selectsRange || !endDate || startDate.getTime() === endDate.getTime()) return start;
    return `${start} – ${endDate.toLocaleDateString('pt-BR')}`;
  })();

  return (
    <BottomSheet
      open={open}
      dismiss={dismiss}
      title="Painel de controle"
      className="agro-wbs-maps-control-panel-sheet"
    >
      <div className="agro-wbs-maps-sheet-body">
        <Section
          label="Variável"
          valueText={variableLabel}
          open={openSection === 'variable'}
          onToggle={toggleSection('variable')}
        >
          {variables.length === 0 && (
            <Snackbar className="mt-2">Nenhuma variável disponível.</Snackbar>
          )}
          {variables.map((v) => (
            <OptionRow
              key={v.value}
              label={v.label}
              selected={v.value === variable}
              onSelect={() => {
                setVariable(v.value);
                setOpenSection('');
              }}
            />
          ))}
        </Section>

        <Section
          label="Período de análise"
          valueText={periodLabel}
          open={openSection === 'period'}
          onToggle={toggleSection('period')}
        >
          {disabled && (
            <Snackbar className="mt-2">Selecione a variável primeiro.</Snackbar>
          )}
          {!disabled && selectsRange && (
            <DatePicker
              inline
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={changePeriodRange}
              includeDateIntervals={dateIntervals}
            />
          )}
          {!disabled && !selectsRange && (
            <DatePicker
              inline
              selected={startDate}
              onChange={changePeriod}
              includeDateIntervals={dateIntervals}
            />
          )}
        </Section>
      </div>

      <div className="agro-wbs-maps-sheet-footer">
        <SheetActionButton variant="confirm" onClick={dismiss}>
          Concluir
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
